import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { AST, Relation } from '@cotera/era';
import { assertOk } from '@cotera/utilities';
import { useSuspenseQuery } from '@tanstack/react-query';
import { err, ok, Result } from 'neverthrow';
import { useAppData, useWhoami } from '../../stores/org';
import { DuckDBQueryResult } from '../duckdb';
import { useApi } from './api';
import { DataProvider } from './fn.type';

export type QueryData = {
  data: DuckDBQueryResult;
  artifactRel: Relation;
  fromArtifactId?: string;
};

export const useDefinition: DataProvider<
  {
    id: string;
  },
  Result<
    AST.RelFR,
    {
      errorType: 'NotFound';
      message: string;
    }
  >
> = ({ id }, _opts) => {
  const api = useApi();
  const orgId = useWhoami((x) => x.org.id);
  const manifestMeta = useAppData((x) => x.manifestMeta);

  return useSuspenseQuery({
    queryKey: queryKeys.manifest.app({
      id,
      version: manifestMeta.version,
      orgId,
    }),
    queryFn: async () => {
      if (manifestMeta.definitions[id] === undefined) {
        return err({
          errorType: 'NotFound' as const,
          message: `Definition for id: ${id} not found`,
        });
      }
      const res = assertOk(await api.t.devServer.manifests.definition({ id }));

      if (res === null) {
        return err({
          errorType: 'NotFound' as const,
          message: `Definition for id: ${id} not found`,
        });
      }

      const { ast } = Relation.wrap(res);
      return ok(ast);
    },
  });
};
